import React, { useState, useMemo, useRef } from 'react';
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  Divider,
  Tag,
  message,
  Input,
  DatePicker,
} from 'antd';
import {
  FileExcelOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { formatCurrency } from '../withdrawal-check/utils';
import { TransactionResponse } from '../../model/Transaction.model';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface WithdrawalCardMultiExportModalProps {
  visible: boolean;
  onClose: () => void;
  transactions: TransactionResponse[];
  cardInfo: {
    cardNumber: string;
    cardHolderName: string;
    customerName: string;
    bankName: string;
    cardType: string;
  };
  selectedYear: number;
}

const WithdrawalCardMultiExportModal: React.FC<
  WithdrawalCardMultiExportModalProps
> = ({ visible, onClose, transactions, cardInfo, selectedYear }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);

  // Add table key to force re-render when modal opens
  const [tableKey, setTableKey] = useState(0);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});

  // Ref to track current filtered data
  const tableRef = useRef<any>(null);
  const [currentFilteredData, setCurrentFilteredData] = useState<
    TransactionResponse[]
  >([]);

  // Reset when modal opens
  React.useEffect(() => {
    if (visible) {
      setTableKey((prev) => prev + 1);
      setSelectedRowKeys([]);
      setFilteredInfo({});
      setSortedInfo({});
    }
  }, [visible]);

  // Filter valid transactions (exclude deleted or zero amounts)
  const validTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      return transaction.amount > 0;
    });
  }, [transactions]);

  // Update filtered data when valid transactions change
  React.useEffect(() => {
    setCurrentFilteredData(validTransactions);
  }, [validTransactions]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'PENDING':
        return 'Đang chờ';
      default:
        return status;
    }
  };

  // Get date range for current year
  const currentYearRange = useMemo(() => {
    const startOfYear = dayjs().year(selectedYear).startOf('year');
    const endOfYear = dayjs().year(selectedYear).endOf('year');
    return [startOfYear, endOfYear];
  }, [selectedYear]);

  // Handle table change to track filtered data
  const handleTableChange = (
    pagination: any,
    filters: any,
    sorter: any,
    extra: any
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    // Update current filtered data for all actions
    if (extra.currentDataSource) {
      setCurrentFilteredData(extra.currentDataSource);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentFilteredData(validTransactions);
    // Không xóa selectedRowKeys - giữ nguyên selections
    setTableKey((prev) => prev + 1); // Force table re-render
    message.success('Đã xóa tất cả bộ lọc');
  };

  // Search functionality for columns
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string
  ) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (
    dataIndex: string,
    placeholder: string
  ): ColumnType<TransactionResponse> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo[dataIndex] || null,
    onFilter: (value, record) => {
      let searchValue = '';

      switch (dataIndex) {
        case 'amount':
          searchValue = record.amount.toString();
          break;
        case 'feeForCustomer':
          searchValue = (record.feeForCustomer || 0).toString();
          break;
        case 'thanhTien':
          // Calculate thành tiền for search
          const { amount, feeForCustomer, customerType } = record;
          let thanhTien = 0;
          if (feeForCustomer) {
            if (customerType === 'vn') {
              thanhTien = feeForCustomer;
            } else {
              thanhTien = amount * (feeForCustomer / 100);
            }
          }
          searchValue = thanhTien.toString();
          break;
        case 'feeApplied':
          searchValue = (record.feeApplied || 0).toString();
          break;
        case 'commissionRateForPartner':
          searchValue = (record.commissionRateForPartner || 0).toString();
          break;
        case 'balance':
          searchValue = record.balance.toString();
          break;
        default:
          searchValue = '';
      }

      return searchValue
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // Date filter for transaction date column
  const getDateFilterProps = (): ColumnType<TransactionResponse> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <RangePicker
          value={
            selectedKeys[0]
              ? JSON.parse(selectedKeys[0] as string).map((date: string) =>
                  dayjs(date)
                )
              : null
          }
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setSelectedKeys([
                JSON.stringify([
                  dates[0].format('YYYY-MM-DD'),
                  dates[1].format('YYYY-MM-DD'),
                ]),
              ]);
            } else {
              setSelectedKeys([]);
            }
          }}
          disabledDate={(current) => {
            const [startOfYear, endOfYear] = currentYearRange;
            return (
              current &&
              (current.isBefore(startOfYear, 'day') ||
                current.isAfter(endOfYear, 'day'))
            );
          }}
          style={{ marginBottom: 8, display: 'block' }}
          placeholder={['Từ ngày', 'Đến ngày']}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Lọc
          </Button>
          <Button
            onClick={() => clearFilters && clearFilters()}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    filteredValue: filteredInfo.transactionDate || null,
    onFilter: (value, record) => {
      if (!value) return true;

      const [startDate, endDate] = JSON.parse(value as string);
      const recordDate = dayjs(record.transactionDate);

      return (
        recordDate.isAfter(dayjs(startDate).subtract(1, 'day')) &&
        recordDate.isBefore(dayjs(endDate).add(1, 'day'))
      );
    },
  });

  // Calculate preview totals for selected records
  const previewData = useMemo(() => {
    const selectedRecords = validTransactions.filter((record) =>
      selectedRowKeys.includes(record.id)
    );

    const totals = selectedRecords.reduce(
      (acc, record) => {
        const amount = record.amount || 0;
        const feeForCustomer = record.feeForCustomer || 0;
        const customerType = record.customerType || '%';
        const feeApplied = record.feeApplied || 0;
        const commissionRateForPartner = record.commissionRateForPartner || 0;
        const balance = record.balance || 0;

        // Calculate thành tiền
        let thanhTien = 0;
        if (feeForCustomer) {
          if (customerType === 'vn') {
            thanhTien = feeForCustomer;
          } else {
            thanhTien = amount * (feeForCustomer / 100);
          }
        }

        // Calculate POS fee amount
        const posFeeAmount = (amount * feeApplied) / 100;

        // Calculate CTV commission amount
        const ctvCommissionAmount = (amount * commissionRateForPartner) / 100;

        // Calculate HH based on customer fee type
        let hh = 0;
        if (customerType === 'vn') {
          // Phí KH là VND: HH = phí thu - (phí CTV% * Rút)
          hh = feeForCustomer - (commissionRateForPartner / 100) * amount;
        } else {
          // Phí KH là %: HH = (phí thu% * Rút) - (phí CTV% * Rút)
          hh =
            (feeForCustomer / 100) * amount -
            (commissionRateForPartner / 100) * amount;
        }

        return {
          count: acc.count + 1,
          totalAmount: acc.totalAmount + amount,
          totalThanhTien: acc.totalThanhTien + thanhTien,
          totalPosFee: acc.totalPosFee + posFeeAmount,
          totalCtvCommission: acc.totalCtvCommission + ctvCommissionAmount,
          totalBalance: acc.totalBalance + balance,
          totalHH: acc.totalHH + hh,
        };
      },
      {
        count: 0,
        totalAmount: 0,
        totalThanhTien: 0,
        totalPosFee: 0,
        totalCtvCommission: 0,
        totalBalance: 0,
        totalHH: 0,
      }
    );

    return totals;
  }, [validTransactions, selectedRowKeys]);

  const columns: ColumnsType<TransactionResponse> = [
    {
      title: 'Chọn',
      key: 'select',
      width: 60,
      render: (_: any, record: TransactionResponse) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.id]);
            } else {
              setSelectedRowKeys(
                selectedRowKeys.filter((key) => key !== record.id)
              );
            }
          }}
        />
      ),
    },
    {
      title: 'Ngày GD',
      key: 'transactionDate',
      dataIndex: 'transactionDate',
      width: 120,
      render: (_: any, record: TransactionResponse) => (
        <span>{dayjs(record.transactionDate).format('DD/MM/YYYY')}</span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix(),
      ...getDateFilterProps(),
    },
    {
      title: 'Số tiền rút',
      key: 'amount',
      dataIndex: 'amount',
      width: 140,
      render: (_: any, record: TransactionResponse) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(record.amount)}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        a.amount - b.amount,
      ...getColumnSearchProps('amount', 'Tìm theo số tiền...'),
    },
    {
      title: 'Phí KH',
      key: 'feeForCustomer',
      dataIndex: 'feeForCustomer',
      width: 120,
      render: (_: any, record: TransactionResponse) => {
        const { feeForCustomer, customerType } = record;
        if (!feeForCustomer || feeForCustomer === 0) {
          return <span className="text-gray-400">0</span>;
        }

        if (customerType === 'vn') {
          return (
            <span className="text-blue-500 font-medium text-sm">
              {formatCurrency(feeForCustomer)}
            </span>
          );
        } else {
          return (
            <span className="text-orange-500 font-medium text-sm">
              {feeForCustomer.toFixed(2)}%
            </span>
          );
        }
      },
      ...getColumnSearchProps('feeForCustomer', 'Tìm theo phí KH...'),
    },
    {
      title: 'Thành tiền',
      key: 'thanhTien',
      dataIndex: 'thanhTien',
      width: 120,
      render: (_: any, record: TransactionResponse) => {
        const { amount, feeForCustomer, customerType } = record;
        let totalAmount = 0;

        if (feeForCustomer) {
          if (customerType === 'vn') {
            totalAmount = feeForCustomer;
          } else {
            totalAmount = amount * (feeForCustomer / 100);
          }
        }

        return (
          <span className="font-semibold text-purple-600">
            {formatCurrency(totalAmount)}
          </span>
        );
      },
      sorter: (a: TransactionResponse, b: TransactionResponse) => {
        const getTotalAmount = (record: TransactionResponse) => {
          const { amount, feeForCustomer, customerType } = record;
          if (!feeForCustomer) return 0;
          return customerType === 'vn'
            ? feeForCustomer
            : amount * (feeForCustomer / 100);
        };
        return getTotalAmount(a) - getTotalAmount(b);
      },
      ...getColumnSearchProps('thanhTien', 'Tìm theo thành tiền...'),
    },
    {
      title: 'Phí POS (%)',
      key: 'feeApplied',
      dataIndex: 'feeApplied',
      width: 100,
      render: (_: any, record: TransactionResponse) => (
        <span className="text-orange-500">
          {record.feeApplied ? `${record.feeApplied}%` : 'N/A'}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        (a.feeApplied || 0) - (b.feeApplied || 0),
      ...getColumnSearchProps('feeApplied', 'Tìm theo phí POS...'),
    },
    {
      title: 'Phí CTV (%)',
      key: 'commissionRateForPartner',
      dataIndex: 'commissionRateForPartner',
      width: 100,
      render: (_: any, record: TransactionResponse) => (
        <span className="text-purple-500">
          {record.commissionRateForPartner
            ? `${record.commissionRateForPartner}%`
            : 'N/A'}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        (a.commissionRateForPartner || 0) - (b.commissionRateForPartner || 0),
      ...getColumnSearchProps(
        'commissionRateForPartner',
        'Tìm theo phí CTV...'
      ),
    },
    {
      title: 'Thực nhận',
      key: 'balance',
      dataIndex: 'balance',
      width: 120,
      render: (_: any, record: TransactionResponse) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(record.balance)}
        </span>
      ),
      sorter: (a: TransactionResponse, b: TransactionResponse) =>
        a.balance - b.balance,
      ...getColumnSearchProps('balance', 'Tìm theo thực nhận...'),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      dataIndex: 'status',
      width: 100,
      render: (_: any, record: TransactionResponse) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusText(record.status)}
        </Tag>
      ),
      filters: [
        { text: 'Hoàn thành', value: 'COMPLETED' },
        { text: 'Đang chờ', value: 'PENDING' },
      ],
      onFilter: (value: any, record: TransactionResponse) =>
        record.status === value,
      filteredValue: filteredInfo.status || null,
    },
  ];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Chỉ thêm những record trong filtered data mà chưa được chọn
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = Array.from(
        new Set([...selectedRowKeys, ...currentIds])
      );
      setSelectedRowKeys(newSelections);
    } else {
      // Chỉ bỏ chọn những record trong filtered data, giữ lại những record khác
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = selectedRowKeys.filter(
        (id) => !currentIds.includes(id)
      );
      setSelectedRowKeys(newSelections);
    }
  };

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một giao dịch để xuất Excel');
      return;
    }

    try {
      setLoading(true);
      const selectedRecords = validTransactions.filter((record) =>
        selectedRowKeys.includes(record.id)
      );

      // Import and use the card withdrawal export service
      const { CardWithdrawalExportService } = await import(
        '../../utils/excel/cardWithdrawalExportService'
      );

      await CardWithdrawalExportService.exportToExcel({
        records: selectedRecords,
        cardInfo,
        month: 0, // Indicate cả năm
        year: selectedYear,
      });

      message.success(
        `Đã xuất Excel thành công cho ${selectedRecords.length} giao dịch`
      );
      onClose();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedRowKeys([]);
    setSearchedColumn('');
    setTableKey((prev) => prev + 1);
    onClose();
  };

  const formatCardNumber = (cardNumber?: string) => {
    if (!cardNumber) return 'N/A';
    return `****${cardNumber.slice(-4)}`;
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <FileExcelOutlined className="text-green-600" />
          <span>Xuất Excel giao dịch rút tiền thẻ</span>
        </div>
      }
      open={visible}
      onCancel={handleModalClose}
      width={1400}
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={handleModalClose}>
          Hủy
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={loading}
          disabled={selectedRowKeys.length === 0}
        >
          Xuất Excel ({selectedRowKeys.length} giao dịch đã chọn)
        </Button>,
      ]}
      className="withdrawal-card-multi-export-modal"
    >
      <div className="space-y-4">
        {/* Card Info */}
        <Card size="small" className="bg-blue-50 border-blue-200">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <Text strong>Thông tin thẻ: </Text>
                <Tag color="blue" className="text-base">
                  {formatCardNumber(cardInfo.cardNumber)}
                </Tag>
              </div>
              <div>
                <Text strong>Kỳ: </Text>
                <Tag color="green">Năm {selectedYear}</Tag>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Text type="secondary">Chủ thẻ: </Text>
                <Text strong>{cardInfo.cardHolderName}</Text>
              </div>
              <div>
                <Text type="secondary">Khách hàng: </Text>
                <Text strong className="text-green-600">
                  {cardInfo.customerName}
                </Text>
              </div>
              <div>
                <Text type="secondary">Ngân hàng: </Text>
                <Text strong>{cardInfo.bankName}</Text>
              </div>
              <div>
                <Text type="secondary">Loại thẻ: </Text>
                <Text strong>{cardInfo.cardType}</Text>
              </div>
            </div>
          </div>
        </Card>

        {/* Search hint and controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Text type="secondary" className="text-xs">
              💡 Mẹo: Click icon tìm kiếm ở header cột để lọc chi tiết
            </Text>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearAllFilters}
              type="default"
              size="small"
              className="text-red-500 hover:text-red-700 hover:border-red-300 border-red-200"
              title="Xóa tất cả bộ lọc"
            >
              <span className="text-xs">Xóa tất cả bộ lọc</span>
            </Button>
          </Space>
          <Text type="secondary">
            Hiển thị: {currentFilteredData.length}/{validTransactions.length}{' '}
            giao dịch
          </Text>
        </div>

        {/* Selection Controls */}
        <div className="flex items-center justify-between">
          <Space>
            <Checkbox
              checked={
                currentFilteredData.length > 0 &&
                currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              indeterminate={
                selectedRowKeys.length > 0 &&
                currentFilteredData.some((record) =>
                  selectedRowKeys.includes(record.id)
                ) &&
                !currentFilteredData.every((record) =>
                  selectedRowKeys.includes(record.id)
                )
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Chọn hiển thị
            </Checkbox>
            <Text type="secondary">
              Đã chọn: {selectedRowKeys.length} giao dịch | Hiển thị:{' '}
              {
                currentFilteredData.filter((record) =>
                  selectedRowKeys.includes(record.id)
                ).length
              }
              /{currentFilteredData.length}
            </Text>
          </Space>
          {Object.keys(filteredInfo).length > 0 && (
            <Tag color="orange" className="text-sm">
              Đang áp dụng bộ lọc
            </Tag>
          )}
        </div>

        {/* Table */}
        <Table
          ref={tableRef}
          columns={columns}
          dataSource={validTransactions}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} giao dịch`,
          }}
          scroll={{ y: 400, x: 1200 }}
          size="small"
          className="border border-gray-200"
          key={tableKey}
          onChange={handleTableChange}
        />

        {/* Preview Summary */}
        {selectedRowKeys.length > 0 && (
          <>
            <Divider />
            <Card
              size="small"
              title={
                <div className="flex items-center space-x-2">
                  <EyeOutlined />
                  <span>Xem trước tổng hợp</span>
                </div>
              }
              className="bg-green-50 border-green-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <Text type="secondary">Số giao dịch:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {previewData.count} giao dịch
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng tiền rút:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {formatCurrency(previewData.totalAmount)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng thành tiền:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalThanhTien)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí POS:</Text>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(previewData.totalPosFee)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí CTV:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalCtvCommission)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng thực nhận:</Text>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(previewData.totalBalance)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng HH:</Text>
                  <div className="text-lg font-semibold text-emerald-600">
                    {formatCurrency(previewData.totalHH)}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
};

export default WithdrawalCardMultiExportModal;
